import React, {useEffect, useState} from 'react';
import './App.css';
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import LastPostsPage from "./pages/LastPostsPage";
import SinglePostPage from "./pages/SinglePostPage";
import AuthorPage from "./pages/AuthorPage";
import TopLayout from "./pages/TopLayout";
import TopLast24hPage from "./pages/TopLast24hPage";
import TopThisMonthPage from "./pages/TopThisMonthPage";
import TopThisYearPage from "./pages/TopThisYearPage";
import VerbisPage from "./pages/VerbisPage";
import EditorPage from "./pages/EditorPage";
import ModerationLayout from "./pages/ModerationLayout";
import ModerationFacebookPage from "./pages/ModerationFacebookPage";
import ModerationCorrectionsPage from "./pages/ModerationCorrectionsPage";
import ModerationCommentsPage from "./pages/ModerationCommentsPage";
import ModerationTrashPage from "./pages/ModerationTrashPage";
import SearchPage from "./pages/SearchPage";
import HuntPage from "./pages/HuntPage";
import ErrorPage from "./pages/ErrorPage";
import {UserContext} from "./contexts/UserContext";
import { getMyUserInfo, getCurrentApplicationPassword } from "./services/users";
import { User } from "./services/types";
import {UserCredentials} from "./services/wpAuth";
import {getStoredCredentials} from "./utils/auth";
import {LikesContext} from "./contexts/LikesContext";
import {useMatomo} from "./hooks/useMatomo";
import UpdateToast from "./components/UpdateToast";

const router = createBrowserRouter([
    {
        path: "/",
        element: <Navigate to={"/last" + window.location.search}/>
    },
    {
        path: "/post",
        element: <SinglePostPage />,
        errorElement: <ErrorPage />
    },
    {
        path: "/last",
        element: <LastPostsPage />,
        errorElement: <ErrorPage />
    },
    {
        path: "/author/:authorId",
        element: <AuthorPage />,
        errorElement: <ErrorPage />
    },
    {
        path: "/top",
        element: <TopLayout />,
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <Navigate to="/top/last24h" replace />
            },
            {
                path: "last24h",
                element: <TopLast24hPage />,
                errorElement: <ErrorPage />
            },
            {
                path: "thismonth",
                element: <TopThisMonthPage />,
                errorElement: <ErrorPage />
            },
            {
                path: "thisyear",
                element: <TopThisYearPage />,
                errorElement: <ErrorPage />
            }
        ]
    },
    {
        path: "/verbis",
        element: <VerbisPage />,
        errorElement: <ErrorPage />
    },
    {
        path: "/search",
        element: <SearchPage />,
        errorElement: <ErrorPage />
    },
    {
        path: "/editor",
        element: <EditorPage />,
        errorElement: <ErrorPage />,
    },
    {
        path: "/hunt",
        element: <HuntPage />,
        errorElement: <ErrorPage />,
    },
    {
        path: "/moderation",
        element: <ModerationLayout />,
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <Navigate to="/moderation/facebook" replace />
            },
            {
                path: "facebook",
                element: <ModerationFacebookPage />,
                errorElement: <ErrorPage />
            },
            {
                path: "corrections",
                element: <ModerationCorrectionsPage />,
                errorElement: <ErrorPage />
            },
            {
                path: "comments",
                element: <ModerationCommentsPage />,
                errorElement: <ErrorPage />
            },
            {
                path: "trash",
                element: <ModerationTrashPage />,
                errorElement: <ErrorPage />
            }
        ]
    }
], {
    basename: __APP_BASE_PATH__.replace(/\/$/, ''), // Remove trailing slash for router
})

function App() {
    useMatomo(router);

    const [currentUser, setCurrentUser] = useState<User | null>(null);
    
    const isModerator = (): boolean => {
        return currentUser?.roles?.includes('administrator') || 
               currentUser?.roles?.includes('birds_modo') || 
               false;
    };

    const userContextValue = { 
        currentUser, 
        setCurrentUser,
        isModerator
    };
    
    const [likes, setLikes] = useState<number[]>([]);
    const likesContextValue = { likes, setLikes };

    const initLikes = () => {
        const storedLikes : string | null = localStorage.getItem("likes");
        if(storedLikes !== null){
            setLikes(JSON.parse(storedLikes) as Array<number>);
        }else{
            localStorage.setItem("likes", JSON.stringify(likes));
        }
    }

    const initCurrentUser = () => {
        const userCredentials = getStoredCredentials();
        if(currentUser === null && userCredentials !== null){
            getMyUserInfo(userCredentials).then((response) => {
                setCurrentUser(response);
            }).catch((reason:any) => {
                console.error("Unable to get user info from credentials: " + JSON.stringify(userCredentials));
            })
        }
    }

    const handleAuthentication = () => {
        const searchParams : URLSearchParams = new URLSearchParams(window.location.search);
        if(searchParams.get("do") === "auth"){
            let newUserCredentials : UserCredentials = {
                uuid: null,
                site_url: searchParams.get("site_url"),
                user_login: searchParams.get("user_login"),
                password: searchParams.get("password"),
            }
            getCurrentApplicationPassword(newUserCredentials).then((appPassword) => {
                newUserCredentials.uuid = appPassword.uuid;
                localStorage.setItem("user_credentials", JSON.stringify(newUserCredentials));
                initCurrentUser();
                window.history.replaceState({}, document.title, window.location.pathname);
            }).catch((reason) => {
                console.error("Unable to retrieve application password: " + JSON.stringify(newUserCredentials));
            })
        }
        if(searchParams.get("do") === "disconnect"){
            localStorage.removeItem("user_credentials");
            setCurrentUser(null);
        }
    }

    useEffect(() => {
        initCurrentUser();
        handleAuthentication();
        initLikes();
    }, []);

    return (
      <div className={"App"}>
          <UserContext.Provider value={userContextValue}>
              <LikesContext.Provider value={likesContextValue}>
                  <RouterProvider router={router}/>
                  <UpdateToast />
              </LikesContext.Provider>
          </UserContext.Provider>
      </div>
    );
}

export default App;
